/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { AsyncPipe, DOCUMENT } from "@angular/common";
import {
	Component,
	computed,
	inject,
	OnDestroy,
	OnInit,
	signal,
	TemplateRef,
	ViewChild
} from "@angular/core";
import { FormControl, ReactiveFormsModule } from "@angular/forms";
import { ColDef, GridApi, GridOptions, GridReadyEvent, IRowNode } from "ag-grid-community";
import { EChartsOption, EChartsType } from "echarts";
import { CallbackDataParams, TopLevelFormatterParams } from "echarts/types/dist/shared";
import {
	BehaviorSubject,
	catchError,
	combineLatest,
	defer,
	EMPTY,
	filter,
	finalize,
	map,
	Observable,
	Subject,
	switchMap,
	take,
	takeUntil,
	tap,
	throwError,
	timer
} from "rxjs";
import { ButtonComponent } from "../../../shared/components/button/button.component";
import { ChartComponent } from "../../../shared/components/chart/chart.component";
import { DropdownComponent } from "../../../shared/components/dropdown/dropdown.component";
import { InputComponent } from "../../../shared/components/input/input.component";
import { KpiComponent } from "../../../shared/components/kpi/kpi.component";
import { SpinnerComponent } from "../../../shared/components/spinner/spinner.component";
import { PaginationComponent } from "../../../shared/components/table/pagination/pagination.component";
import { TableComponent } from "../../../shared/components/table/table.component";
import { UploadFileComponent } from "../../../shared/components/upload-file/upload-file.component";
import { AiEmissionInfo } from "../../../shared/models/http/ai/ai-emission-info.model";
import { EnvironmentalImpactByModel } from "../../../shared/models/http/ai/ai-environmental-impact-by-model.model";
import { EnvironmentalImpact } from "../../../shared/models/http/ai/ai-environmental-impact.model";
import { AiFileStatusResponse } from "../../../shared/models/http/ai/ai-file-status-response.model";
import { IctCarbonInsightsReportTab } from "../../../shared/models/http/dashboard/ict-carbon-insights-report.model";
import { WaterUsageByModel } from "../../../shared/models/http/ai/ai-water-usage-by-model.model";
import { PieChartSlice } from "../../../shared/models/http/charts/pie-chart/pie-chart-slice.model";
import { PieChart } from "../../../shared/models/http/charts/pie-chart/pie-chart.model";
import { RolePermission } from "../../../shared/models/ui/authorization.model";
import { ChartResponse } from "../../../shared/models/ui/charts.model";
import { ReportingPeriodValue } from "../../../shared/models/ui/reporting-period.model";
import { SelectOption } from "../../../shared/models/ui/select-option.model";
import { AuthorizationService } from "../../../shared/services/authorization.service";
import { DialogService } from "../../../shared/services/dialog.service";
import { IctCarbonInsightsReportService } from "../../../shared/services/ict-carbon-insights-report.service";
import { DashboardAIHttpService } from "../../../shared/services/http/dashboard-ai-http.service";
import { StorageService } from "../../../shared/services/storage.service";
import { ToastService } from "../../../shared/services/toast.service";
import { COLORS } from "../../../shared/utils/constants";
import {
	getChartTooltip,
	getHardwareAssetsTotalEmissionOverridesForPieChart
} from "../../../shared/utils/echarts/chart-utils";
import { GRANTS } from "../../../shared/utils/grants.constants";
import { lazyLoad } from "../../../shared/utils/lazy-load";
import {
	getDefaultReportingPeriodValue,
	getReportingPeriodOptions,
	getReportingPeriodRange
} from "../../../shared/utils/reporting-period";
import {
	formatDateForBE,
	formatNumberMaxDecimals,
	roundValueUpTo,
	saveAs
} from "../../../shared/utils/utils";

enum PageStates {
	UPLOAD = "UPLOAD",
	LOADING = "LOADING",
	ERROR = "ERROR",
	MAIN = "MAIN"
}

interface CompanyImpactRow {
	company: string;
	models: number;
	emissionValue: number;
	total: number;
}

interface ModelImpactRow {
	company: string;
	model: string;
	emissionValue: number;
	emissionPerToken: number;
	total: number;
}

interface WaterUsageByModelRow {
	model: string;
	company: string;
	totalWaterUsage: number;
	waterUsagePerToken: number;
	total: number;
}

interface ModelEmissionMetric {
	unit?: string;
}

interface ModelEmissionRow {
	energy?: ModelEmissionMetric;
	emission?: ModelEmissionMetric;
	water?: ModelEmissionMetric;
	wu?: ModelEmissionMetric;
	waterUsage?: ModelEmissionMetric;
}

@Component({
	selector: "globalictcc-ai-page",
	standalone: true,
	imports: [
		AsyncPipe,
		ButtonComponent,
		ChartComponent,
		DropdownComponent,
		InputComponent,
		KpiComponent,
		PaginationComponent,
		ReactiveFormsModule,
		SpinnerComponent,
		TableComponent,
		UploadFileComponent
	],
	templateUrl: "./ai.component.html",
	styleUrl: "./ai.component.scss"
})
export class AiPageComponent implements OnInit, OnDestroy {
	private readonly _dashboardAIHttpService = inject(DashboardAIHttpService);
	private readonly _storageService = inject(StorageService);
	private readonly _dialogService = inject(DialogService);
	private readonly _toastService = inject(ToastService);
	private readonly _ictCarbonInsightsReportService = inject(IctCarbonInsightsReportService);
	public readonly authorizationService = inject(AuthorizationService);
	public readonly grants = GRANTS.ai;
	public readonly permissions = RolePermission;

	@ViewChild("uploadDeviceList") public uploadDeviceListDialogContent: TemplateRef<unknown>;
	@ViewChild("rename") public renameDialogContent: TemplateRef<unknown>;

	public _PageStates = PageStates;
	public _pageState = signal<PageStates>(PageStates.MAIN);

	public sameNameErrorMessage = signal<string>("");
	public uploadedFile = signal<File | undefined>(undefined);

	public isUploadDisabled = computed(() => !!this.sameNameErrorMessage() || !this.uploadedFile());

	// Form Controllers
	public fileControl = new FormControl("");
	public renameFileControl = new FormControl("");

	public filesOptions: SelectOption[] = [];
	public selectedFile = signal<string>("");
	public appsPerformanceChartColors: string[] = [
		COLORS.TURQUOISE_100,
		COLORS.GREY_50,
		COLORS.TURQUOISE_50,
		COLORS.YELLOW_100,
		COLORS.ORANGE_50
	];
	public appsPerformanceChartOverrides: EChartsOption = this._buildLineChartOverrides("KgCO2");
	public appsWaterConsumptionChartColors: string[] = [
		COLORS.FUTURE_BLUE,
		COLORS.FUTURE_BLUE_50,
		COLORS.TURQUOISE,
		COLORS.TURQUOISE_50,
		COLORS.YELLOW_50
	];
	public appsWaterConsumptionChartOverrides: EChartsOption = this._buildLineChartOverrides("L");
	public companyOptions: SelectOption[] = [];
	public companyControl = new FormControl<string[]>([]);
	public totalImpactTableHasError: boolean = false;
	public totalImpactPieChart?: ChartResponse<PieChart>;
	public totalImpactPieChartColors: string[] = [];
	public totalImpactPieChartOverrides: EChartsOption = this._buildTotalImpactPieChartOverrides(0);
	public totalImpactGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Company", field: "company", sortable: true },
			{ headerName: "# of models", field: "models", sortable: true },
			{
				headerName: "Total emissions",
				field: "emissionValue",
				sortable: true,
				valueFormatter: params =>
					this._formatTotalImpactEmissionValue(Number(params.value ?? 0))
			}
		],
		rowData: [],
		getRowStyle: params => {
			if (!params?.data?.total) return undefined;

			const percentage = (params.data.emissionValue / params.data.total) * 100;

			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onTotalImpactGridReady.bind(this)
	};

	public modelOptions: SelectOption[] = [];
	public modelControl = new FormControl<string[]>([]);
	public byModelTableHasError: boolean = false;
	public byModelPieChart?: ChartResponse<PieChart>;
	public byModelPieChartColors: string[] = [];
	public byModelPieChartOverrides: EChartsOption = this._buildTotalImpactPieChartOverrides(0);
	public byModelGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Model", field: "model", sortable: true },
			{ headerName: "Company", field: "company", sortable: true },
			{
				headerName: "Total Emissions",
				field: "emissionValue",
				sortable: true,
				valueFormatter: params =>
					this._formatTotalImpactEmissionValue(Number(params.value ?? 0))
			},
			{
				headerName: "Emission per Token",
				field: "emissionPerToken",
				sortable: true,
				valueFormatter: params => this._formatByTokenValue(params.value)
			}
		],
		rowData: [],
		getRowStyle: params => {
			if (!params?.data?.total) return undefined;

			const percentage = (params.data.emissionValue / params.data.total) * 100;

			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onByModelGridReady.bind(this)
	};

	public waterUsageModelOptions: SelectOption[] = [];
	public waterUsageModelControl = new FormControl<string[]>([]);
	public waterUsageByModelTableHasError: boolean = false;
	public waterUsageByModelPieChart?: ChartResponse<PieChart>;
	public waterUsageByModelPieChartColors: string[] = [];
	public waterUsageByModelPieChartOverrides: EChartsOption =
		this._buildTotalImpactPieChartOverrides(0);
	public waterUsageByModelGridOptions: GridOptions = {
		columnDefs: [
			{ headerName: "Model", field: "model", sortable: true },
			{ headerName: "Company", field: "company", sortable: true },
			{
				headerName: "Total Water Usage",
				field: "totalWaterUsage",
				sortable: true,
				valueFormatter: params =>
					this._formatTotalImpactEmissionValue(Number(params.value ?? 0))
			},
			{
				headerName: "Water Usage per Token",
				field: "waterUsagePerToken",
				sortable: true,
				valueFormatter: params => this._formatByTokenValue(params.value)
			}
		],
		rowData: [],
		getRowStyle: params => {
			if (!params?.data?.total) return undefined;

			const percentage = (params.data.totalWaterUsage / params.data.total) * 100;

			return {
				background: `linear-gradient(to right, ${COLORS.TEXT_GRAY} ${percentage}%, transparent ${percentage}%)`
			};
		},
		onGridReady: this._onWaterUsageByModelGridReady.bind(this)
	};

	private readonly _modelEmissionsPageSize = 10;
	public modelEmissionsCurrentPage = 0;
	public modelEmissionsTotalPages = 0;
	public modelEmissionsTotalElements = 0;
	public modelEmissionsPageItems = 0;
	public modelEmissionsPageSize = this._modelEmissionsPageSize;

	private _fileId?: string;
	private _tenantId: string;
	private _modelEmissionsGridApi?: GridApi;
	private _latestEmissionsRows: ModelEmissionRow[] = [];
	private _totalImpactGridApi?: GridApi<CompanyImpactRow>;
	private _latestImpactRows: CompanyImpactRow[] = [];
	private _totalImpactUnit: string = "";
	private _byModelGridApi?: GridApi<ModelImpactRow>;
	private _latestByModelRows: ModelImpactRow[] = [];
	private _waterUsageByModelGridApi?: GridApi<WaterUsageByModelRow>;
	private _latestWaterUsageByModelRows: WaterUsageByModelRow[] = [];
	private _emissionByModelUnit: string = "";
	private _emissionByModelTokenUnit: number = 1;
	private _waterByModelUnit: string = "";
	private _waterByModelTokenUnit: number = 1;
	private readonly _fileId$ = new BehaviorSubject<string | undefined>(undefined);
	private readonly _period$ = new BehaviorSubject<ReportingPeriodValue>(
		getDefaultReportingPeriodValue()
	);
	private readonly _modelEmissionsRequest$ = new BehaviorSubject<
		{ fileId: string; page: number } | undefined
	>(undefined);

	private readonly _companyFilter$ = new BehaviorSubject<
		| {
				fileId: string;
				companies: string[];
				refreshOptions: boolean;
				showLoading: boolean;
		  }
		| undefined
	>(undefined);

	private readonly _modelFilter$ = new BehaviorSubject<
		| {
				fileId: string;
				models: string[];
				refreshOptions: boolean;
				showLoading: boolean;
		  }
		| undefined
	>(undefined);

	private readonly _waterModelFilter$ = new BehaviorSubject<
		| {
				fileId: string;
				models: string[];
				refreshOptions: boolean;
				showLoading: boolean;
		  }
		| undefined
	>(undefined);

	private readonly _best10EmissionsRefresh$ = new Subject<void>();
	private readonly _best10WaterRefresh$ = new Subject<void>();

	private readonly _predefinedPieColors = [
		COLORS.GREEN_50,
		COLORS.YELLOW_100,
		COLORS.TURQUOISE_100,
		COLORS.ORANGE_50,
		COLORS.TURQUOISE_50
	];
	private readonly _document: Document = inject(DOCUMENT);

	private _destroy$ = new Subject<void>();
	private readonly _statusPollingIntervalMs = 5000;

	public gridOptions: GridOptions = {
		columnDefs: [
			{
				headerName: "Application",
				sortable: true,
				comparator: (valueA: string = "", valueB: string = "") =>
					valueA.localeCompare(valueB),
				field: "applicationId"
			},
			{
				headerName: "Company",
				sortable: true,
				comparator: (valueA: string = "", valueB: string = "") =>
					valueA.localeCompare(valueB),
				field: "provider.name"
			},
			{
				headerName: "Model",
				sortable: true,
				comparator: (valueA: string = "", valueB: string = "") =>
					valueA.localeCompare(valueB),
				field: "provider.model"
			},
			{
				headerName: "# of tokens",
				sortable: true,
				comparator: (valueA: number, valueB: number) => valueA - valueB,
				field: "nTokens"
			},
			{
				headerValueGetter: () => this._getModelEmissionsColumnHeader("Energy", "energy"),
				field: "energy",
				sortable: true,
				comparator: (valueA, valueB, nodeA: IRowNode, nodeB: IRowNode) =>
					(nodeA.data?.energy?.value ?? 0) - (nodeB.data?.energy?.value ?? 0),
				valueGetter: params => {
					const energy = params.data?.energy;
					if (!energy) return "";

					const value = this._formatNumber(energy.value);
					const baseValue = value;
					return energy.uncertainty != null
						? `${baseValue} (${Math.round(parseFloat(this._formatNumber(energy.uncertainty)))}%)`
						: baseValue;
				}
			},
			{
				headerValueGetter: () =>
					this._getModelEmissionsColumnHeader("Emissions", "emission"),
				field: "emissions",
				sortable: true,
				comparator: (valueA, valueB, nodeA, nodeB) =>
					(nodeA.data?.emission?.value ?? 0) - (nodeB.data?.emission?.value ?? 0),
				valueGetter: params => {
					const emission = params.data?.emission;
					if (!emission) return "";

					const value = this._formatNumber(emission.value);
					const baseValue = value;
					return emission.uncertainty != null
						? `${baseValue} (${Math.round(parseFloat(this._formatNumber(emission.uncertainty)))}%)`
						: baseValue;
				}
			},
			{
				headerValueGetter: () =>
					this._getModelEmissionsColumnHeader("Water usage", "water"),
				sortable: true,
				comparator: (valueA, valueB, nodeA, nodeB) =>
					((nodeA.data?.water ?? nodeA.data?.wu)?.value ?? 0) -
					((nodeB.data?.water ?? nodeB.data?.wu)?.value ?? 0),
				valueGetter: params => {
					const water = params.data?.water ?? params.data?.wu;
					if (!water) return "";

					const value = this._formatNumber(water.value, 2);
					const baseValue = value;
					return water.uncertainty != null
						? `${baseValue} (${Math.round(parseFloat(this._formatNumber(water.uncertainty, 2)))}%)`
						: baseValue;
				}
			}
		],
		rowData: [],
		onGridReady: this._onModelEmissionsGridReady.bind(this)
	};
	public periodControl = new FormControl<ReportingPeriodValue>(getDefaultReportingPeriodValue(), {
		nonNullable: true
	});
	public periodOptions: SelectOption[] = getReportingPeriodOptions();
	public isExporting: boolean = false;
	public readonly generalInformation$ = lazyLoad(this._fileId$, fileId =>
		this._dashboardAIHttpService.getGeneralInformation(this._tenantId, fileId)
	);

	public readonly modelEmissions$ = lazyLoad(this._modelEmissionsRequest$, request =>
		this._dashboardAIHttpService.getFileEmissions(
			this._tenantId,
			request.fileId,
			request.page,
			this._modelEmissionsPageSize
		)
	).pipe(tap(state => this._updateModelEmissionsGrid(state)));

	public readonly appsPerformance$ = lazyLoad(
		combineLatest([this._fileId$, this._period$]).pipe(
			map(([fileId, period]) => {
				if (!fileId) return undefined;
				const { start, end } = getReportingPeriodRange(period);
				return {
					fileId,
					startDate: formatDateForBE(start),
					endDate: formatDateForBE(end)
				};
			})
		),
		filters =>
			this._dashboardAIHttpService.getCo2AppsPerformanceLineChart(
				this._tenantId,
				filters.fileId,
				filters.startDate,
				filters.endDate
			)
	);

	public readonly appsWaterConsumption$ = lazyLoad(
		combineLatest([this._fileId$, this._period$]).pipe(
			map(([fileId, period]) => {
				if (!fileId) return undefined;
				const { start, end } = getReportingPeriodRange(period);
				return {
					fileId,
					startDate: formatDateForBE(start),
					endDate: formatDateForBE(end)
				};
			})
		),
		filters =>
			this._dashboardAIHttpService.getWaterAppsPerformanceLineChart(
				this._tenantId,
				filters.fileId,
				filters.startDate,
				filters.endDate
			)
	);

	public readonly environmentalImpactByCompany$ = lazyLoad(this._companyFilter$, filters =>
		this._dashboardAIHttpService
			.getEnvironmentalImpactByCompany(
				this._tenantId,
				filters.fileId,
				filters.refreshOptions ? [] : filters.companies
			)
			.pipe(
				tap(impact =>
					this._handleEnvironmentalImpactResponse(impact, filters.refreshOptions)
				)
			)
	).pipe(
		tap(state => {
			if (state.hasError) this._handleEnvironmentalImpactError();
			if (this._totalImpactGridApi && !this._totalImpactGridApi.isDestroyed()) {
				if (state.isLoading && this._companyFilter$.value?.showLoading)
					this._totalImpactGridApi.setGridOption("loading", true);
				if (!state.isLoading) this._totalImpactGridApi.setGridOption("loading", false);
			}
		})
	);

	public readonly environmentalImpactByModel$ = lazyLoad(this._modelFilter$, filters =>
		this._dashboardAIHttpService
			.getEnvironmentalImpactByModel(
				this._tenantId,
				filters.fileId,
				filters.refreshOptions ? [] : filters.models
			)
			.pipe(
				tap(impact =>
					this._handleEnvironmentalImpactByModelResponse(impact, filters.refreshOptions)
				)
			)
	).pipe(
		tap(state => {
			if (state.hasError) this._handleEnvironmentalImpactByModelError();
			if (this._byModelGridApi && !this._byModelGridApi.isDestroyed()) {
				if (state.isLoading && this._modelFilter$.value?.showLoading)
					this._byModelGridApi.setGridOption("loading", true);
				if (!state.isLoading) this._byModelGridApi.setGridOption("loading", false);
			}
		})
	);

	public readonly waterUsageByModel$ = lazyLoad(this._waterModelFilter$, filters =>
		this._dashboardAIHttpService
			.getWaterUsageByModel(filters.fileId, filters.refreshOptions ? [] : filters.models)
			.pipe(tap(data => this._handleWaterUsageByModelResponse(data, filters.refreshOptions)))
	).pipe(
		tap(state => {
			if (state.hasError) this._handleWaterUsageByModelError();
			if (this._waterUsageByModelGridApi && !this._waterUsageByModelGridApi.isDestroyed()) {
				if (state.isLoading && this._waterModelFilter$.value?.showLoading)
					this._waterUsageByModelGridApi.setGridOption("loading", true);
				if (!state.isLoading)
					this._waterUsageByModelGridApi.setGridOption("loading", false);
			}
		})
	);

	public readonly best10Emissions$ = lazyLoad(
		this._fileId$,
		fileId => this._dashboardAIHttpService.getBest10ModelsByEmission(fileId),
		this._best10EmissionsRefresh$
	);

	public readonly best10WaterUsage$ = lazyLoad(
		this._fileId$,
		fileId => this._dashboardAIHttpService.getBest10ModelsByWaterUsage(fileId),
		this._best10WaterRefresh$
	);

	ngOnInit() {
		this._tenantId = this._storageService.getTenantId();
		this._fetchData().subscribe();
	}

	ngOnDestroy() {
		this._destroy$.next();
		this._destroy$.complete();
	}

	public onFileUpload(file: File): void {
		const fileName = file.name;

		if (this.filesOptions.find(file => file.label === fileName)) {
			this.sameNameErrorMessage.set(
				"This name is already in use by another report. Please enter a different name."
			);

			return;
		}

		this.uploadedFile.set(file);
	}

	public onRemoveFile(): void {
		this.uploadedFile.set(undefined);
	}

	/**
	 * Initiates the download of an AI data export by calling the corresponding service method.
	 * Subscribes to the observable returned by the service to trigger the download process.
	 *
	 * @remarks
	 * This method exports the current file's emissions data as a CSV.
	 */
	public onDownloadTemplate() {
		this._dashboardAIHttpService
			.downloadTemplate()
			.pipe(
				map(blob => URL.createObjectURL(blob)),
				tap({
					next: (url: string) => saveAs(this._document, url, "template.xlsx")
				})
			)
			.subscribe();
	}

	public onDownloadCsv(): void {
		if (!this._fileId || this.isExporting) return;

		this.isExporting = true;

		defer(() => {
			this._toastService.success(
				"Download started",
				"Your CSV download has started successfully. Please wait patiently while the file is being prepared."
			);
			return this._dashboardAIHttpService.exportFile(this._fileId!);
		})
			.pipe(
				takeUntil(this._destroy$),
				finalize(() => {
					this.isExporting = false;
				})
			)
			.subscribe({
				next: blob => {
					const url = URL.createObjectURL(blob);
					saveAs(this._document, url, "ai_emissions.csv");
					URL.revokeObjectURL(url);
				},
				error: error => {
					console.error("Error exporting AI file:", error);
				}
			});
	}

	public onExportReport(): void {
		const period = this.periodControl.value || getDefaultReportingPeriodValue();
		const { start, end } = getReportingPeriodRange(period);
		const from = formatDateForBE(start);
		const to = formatDateForBE(end);
		if (!from || !to) return;

		this._ictCarbonInsightsReportService
			.downloadReport(IctCarbonInsightsReportTab.AI, { from, to })
			.subscribe();
	}

	public onConfirm(): void {
		const file = this.uploadedFile();
		if (!file) return;

		this._pageState.set(PageStates.LOADING);

		const formData = new FormData();
		formData.append("file", file);

		this._dashboardAIHttpService
			.uploadFile(this._tenantId, formData)
			.pipe(
				switchMap(response => {
					this._fileId = response.fileId;
					return this._pollFileStatus(response.fileId);
				}),
				switchMap(statusResponse => {
					if (statusResponse.status === "DONE") {
						return this._fetchData().pipe(tap(() => this.uploadedFile.set(undefined)));
					}

					return throwError(
						() => new Error(statusResponse.message || "File processing failed")
					);
				}),
				catchError(error => {
					this._pageState.set(PageStates.ERROR);
					console.error("Error processing AI file:", error);
					return EMPTY;
				}),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	public onUploadDeviceList() {
		this._dialogService
			.open({
				data: {
					type: "confirm",
					confirmBtnText: "Ok",
					closeBtnText: "Cancel",
					title: "Upload Device List",
					content: this.uploadDeviceListDialogContent,
					confirmAction: this.onConfirm.bind(this),
					isConfirmActionDisabled: this.isUploadDisabled
				}
			})
			.afterClosed()
			.pipe(tap({ next: () => this.sameNameErrorMessage.set("") }), takeUntil(this._destroy$))
			.subscribe();
	}

	public onFileChange(fileId: string | string[]): void {
		if (Array.isArray(fileId)) {
			console.warn("Multiple file selection is not supported.");
			return;
		}
		this._setActiveFile(fileId);
	}

	public onModelEmissionsPreviousPage(): void {
		if (this.modelEmissionsCurrentPage <= 0) return;

		this.modelEmissionsCurrentPage -= 1;
		this._requestModelEmissionsPage(this.modelEmissionsCurrentPage);
	}

	public onModelEmissionsNextPage(): void {
		if (this.modelEmissionsCurrentPage >= this.modelEmissionsTotalPages - 1) return;

		this.modelEmissionsCurrentPage += 1;
		this._requestModelEmissionsPage(this.modelEmissionsCurrentPage);
	}

	/**
	 * Opens a confirmation dialog for renaming an item.
	 *
	 * The dialog displays a title, content, and two buttons ("Ok" and "Cancel").
	 * If the user confirms, the `_onConfirmRename` method is invoked.
	 *
	 * @remarks
	 * This method utilizes the injected `_dialogService` to present the dialog.
	 */
	public onRename() {
		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Ok",
				closeBtnText: "Cancel",
				title: "Rename",
				content: this.renameDialogContent,
				confirmAction: this._onConfirmRename.bind(this)
			}
		});
	}

	/**
	 * Opens a confirmation dialog to prompt the user before deleting the currently uploaded file.
	 * If the user confirms, the deletion action is executed via the bound `_onConfirmDelete` method.
	 *
	 * @remarks
	 * The dialog displays the name of the uploaded file (if available) in the confirmation message.
	 * The dialog provides "Yes, Delete" and "Cancel" options.
	 */
	public onDelete() {
		// To avoid breaking current state where we use `uploadedFile.name`
		let fileName = this.uploadedFile()?.name;

		if (!fileName) {
			fileName = this.filesOptions.find(file => file.value === this.fileControl.value)?.label;
		}

		this._dialogService.open({
			data: {
				type: "confirm",
				confirmBtnText: "Yes, Delete",
				closeBtnText: "Cancel",
				title: `Are you sure you want to delete "${fileName}"?`,
				confirmAction: this._onConfirmDelete.bind(this)
			}
		});
	}

	public onApply() {
		this._period$.next(this.periodControl.value);
	}

	public onApplyTotalImpactFilter() {
		this._requestCompanyImpact(true, false);
	}

	public onTotalImpactTableRefresh() {
		this._requestCompanyImpact(true, false);
	}

	public onTotalImpactChartRefresh() {
		this._requestCompanyImpact(false, false);
	}

	public onApplyByModelFilter() {
		this._requestModelImpact(true, false);
	}

	public onByModelTableRefresh() {
		this._requestModelImpact(true, false);
	}

	public onByModelChartRefresh() {
		this._requestModelImpact(false, false);
	}

	public onApplyWaterUsageByModelFilter() {
		this._requestWaterUsageByModel(true, false);
	}

	public onWaterUsageByModelTableRefresh() {
		this._requestWaterUsageByModel(true, false);
	}

	public onWaterUsageByModelChartRefresh() {
		this._requestWaterUsageByModel(false, false);
	}

	public onBest10EmissionsChartRefresh() {
		this._best10EmissionsRefresh$.next();
	}

	public onBest10WaterUsageChartRefresh() {
		this._best10WaterRefresh$.next();
	}

	/**
	 * Handles the change event when renaming a file or report.
	 *
	 * Checks if the provided name already exists among the available file options.
	 * If the name is already present, sets an error message and marks the rename control with an error.
	 * Otherwise, clears any existing error messages and errors on the control.
	 *
	 * @param name - The new name entered by the user for the file or report. Can be a string, number, or null.
	 */
	public onRenameChange(name: string | number | null) {
		const isAlreadyPresent = this.filesOptions.map(file => file.label).includes(name as string);
		this.sameNameErrorMessage.set(
			isAlreadyPresent
				? "This name is already in use by another report. Please enter a different name."
				: ""
		);
		this.renameFileControl.setErrors(isAlreadyPresent ? { alreadyPresent: true } : null);
	}

	// Utils
	private _fetchData() {
		this._pageState.set(PageStates.LOADING);

		return this._dashboardAIHttpService.getFiles(this._tenantId).pipe(
			map(filesRes =>
				(filesRes.files || []).map(file => ({ label: file.name, value: file.id }))
			),
			tap({
				next: files => {
					this.filesOptions = files;

					if (!files.length) {
						this._setActiveFile(undefined);
						this._latestEmissionsRows = [];
						if (this._modelEmissionsGridApi) {
							this._modelEmissionsGridApi.setGridOption("rowData", []);
						} else {
							this.gridOptions.rowData = [];
						}
						this.selectedFile.set("");
						this.fileControl.setValue("", { emitEvent: false });
						this._pageState.set(PageStates.UPLOAD);
						return;
					}

					const isFileIdNotPresent = !this.filesOptions.some(
						file => file.value === this._fileId
					);

					const firstFile = files[0]?.value;
					if (!this._fileId || isFileIdNotPresent) {
						this._fileId = firstFile;
					}

					const file = this.filesOptions.find(file => file.value === this._fileId)?.label;
					this.selectedFile.set(file || "");

					this.fileControl.setValue(this._fileId ?? "", { emitEvent: false });

					this._setActiveFile(this._fileId);
					this._pageState.set(PageStates.MAIN);
				},
				error: () => this._pageState.set(PageStates.ERROR)
			}),
			finalize(() => {
				if (this._pageState() === PageStates.LOADING) {
					this._pageState.set(PageStates.MAIN);
				}
			})
		);
	}

	/**
	 * Handles the confirmation of a file rename action.
	 *
	 * Retrieves the new file name from the form control, validates it,
	 * and if valid, calls the service to rename the file. After renaming,
	 * it fetches the updated data to reflect changes in the UI.
	 *
	 * @private
	 */
	private _onConfirmRename() {
		const newName = this.renameFileControl.value as string;

		if (!newName || !this._fileId) return;

		this._dashboardAIHttpService
			.renameFile(this._tenantId, this._fileId, newName)
			.pipe(
				switchMap(() => this._fetchData()),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	/**
	 * Handles the confirmation of a file deletion.
	 *
	 * This method calls the dashboard AI service to delete a file
	 * identified by `_fileId` and `_tenantId`. After successful deletion, it
	 * fetches the updated data. The operation is performed as an observable
	 * sequence, and the subscription is initiated within this method.
	 *
	 * @private
	 */
	private _onConfirmDelete() {
		if (!this._fileId) return;

		this._dashboardAIHttpService
			.deleteFile(this._tenantId, this._fileId)
			.pipe(
				switchMap(() => this._fetchData()),
				takeUntil(this._destroy$)
			)
			.subscribe();
	}

	public onExportChart(chartInstance?: EChartsType, imageName?: string) {
		if (!chartInstance) return;

		const originalOption = chartInstance.getOption();
		const optionWithoutDataZoom = {
			...originalOption,
			dataZoom: []
		};

		chartInstance.setOption(optionWithoutDataZoom, { notMerge: true });

		setTimeout(() => {
			const chartAsPngUrl = chartInstance.getDataURL({ type: "png" });
			saveAs(this._document, chartAsPngUrl, imageName || "chart");
			chartInstance.setOption(originalOption, { notMerge: true });
		}, 300);
	}

	/**
	 * Formats a numeric value with up to three decimal digits.
	 * @param value Value to format.
	 * @param maximumFractionDigits Maximum number of decimal digits to display.
	 * @returns Formatted number string or empty string when value is invalid.
	 */
	private _formatNumber(value: number, maximumFractionDigits = 2): string {
		if (value === null || value === undefined || Number.isNaN(value)) {
			return "";
		}

		return formatNumberMaxDecimals(value, Math.min(maximumFractionDigits, 3));
	}

	private _formatByTokenValue(rawValue: unknown): string {
		const val = Number(rawValue ?? 0);
		if (!Number.isFinite(val)) return "";
		if (val === 0) return "0";
		const formatted =
			Math.abs(val) < 0.001 ? val.toExponential(2) : formatNumberMaxDecimals(val, 2);
		return formatted;
	}

	private _formatTokenUnitLabel(tokenUnit: number): string {
		if (tokenUnit >= 1_000_000) return `${tokenUnit / 1_000_000}M`;
		if (tokenUnit >= 1_000) return `${tokenUnit / 1_000}K`;
		return `${tokenUnit}`;
	}

	private _getMetricHeader(label: string, unit?: string): string {
		return unit ? `${label} (${unit})` : label;
	}

	private _getMetricPerTokenHeader(label: string, unit?: string, tokenUnit?: number): string {
		if (!unit) return label;

		return `${label} (${unit} / ${this._formatTokenUnitLabel(tokenUnit ?? 1)} tokens)`;
	}

	private _updateByModelHeaders(): void {
		const labelMap = new Map<string, string>([
			["emissionValue", this._getMetricHeader("Total Emissions", this._emissionByModelUnit)],
			[
				"emissionPerToken",
				this._getMetricPerTokenHeader(
					"Emission per Token",
					this._emissionByModelUnit,
					this._emissionByModelTokenUnit
				)
			]
		]);

		const api =
			this._byModelGridApi && !this._byModelGridApi.isDestroyed()
				? this._byModelGridApi
				: undefined;
		const colDefs = api?.getColumnDefs();
		if (colDefs) {
			for (const colDef of colDefs) {
				const field = (colDef as ColDef).field;
				if (!field || !labelMap.has(field)) continue;

				(colDef as ColDef).headerName = labelMap.get(field);
			}

			api?.setGridOption("columnDefs", colDefs);
			return;
		}

		for (const colDef of this.byModelGridOptions.columnDefs ?? []) {
			const field = (colDef as ColDef).field;
			if (!field || !labelMap.has(field)) continue;

			(colDef as ColDef).headerName = labelMap.get(field);
		}
	}

	private _updateTotalImpactHeaders(): void {
		const api =
			this._totalImpactGridApi && !this._totalImpactGridApi.isDestroyed()
				? this._totalImpactGridApi
				: undefined;
		const colDefs = api?.getColumnDefs();
		const totalColumnHeader = this._getMetricHeader("Total emissions", this._totalImpactUnit);

		if (colDefs) {
			for (const colDef of colDefs) {
				if ((colDef as ColDef).field !== "emissionValue") continue;
				(colDef as ColDef).headerName = totalColumnHeader;
			}

			api?.setGridOption("columnDefs", colDefs);
			return;
		}

		for (const colDef of this.totalImpactGridOptions.columnDefs ?? []) {
			if ((colDef as ColDef).field !== "emissionValue") continue;
			(colDef as ColDef).headerName = totalColumnHeader;
		}
	}

	private _updateWaterUsageByModelHeaders(): void {
		const labelMap = new Map<string, string>([
			["totalWaterUsage", this._getMetricHeader("Total Water Usage", this._waterByModelUnit)],
			[
				"waterUsagePerToken",
				this._getMetricPerTokenHeader(
					"Water Usage per Token",
					this._waterByModelUnit,
					this._waterByModelTokenUnit
				)
			]
		]);

		const api =
			this._waterUsageByModelGridApi && !this._waterUsageByModelGridApi.isDestroyed()
				? this._waterUsageByModelGridApi
				: undefined;
		const colDefs = api?.getColumnDefs();
		if (colDefs) {
			for (const colDef of colDefs) {
				const field = (colDef as ColDef).field;
				if (!field || !labelMap.has(field)) continue;

				(colDef as ColDef).headerName = labelMap.get(field);
			}

			api?.setGridOption("columnDefs", colDefs);
			return;
		}

		for (const colDef of this.waterUsageByModelGridOptions.columnDefs ?? []) {
			const field = (colDef as ColDef).field;
			if (!field || !labelMap.has(field)) continue;

			(colDef as ColDef).headerName = labelMap.get(field);
		}
	}

	private _updateByModelPerTokenHeader(): void {
		this._updateByModelHeaders();
	}

	private _updateWaterUsageByModelPerTokenHeader(): void {
		this._updateWaterUsageByModelHeaders();
	}

	/**
	 * Polls the backend for the status of a recently uploaded file until it completes or fails.
	 * @param fileId - Identifier of the uploaded file to monitor.
	 * @private
	 */
	private _pollFileStatus(fileId: string): Observable<AiFileStatusResponse> {
		return timer(0, this._statusPollingIntervalMs).pipe(
			switchMap(() => this._dashboardAIHttpService.getFileStatus(this._tenantId, fileId)),
			filter(
				(response: AiFileStatusResponse) =>
					response.status === "DONE" || response.status === "ERROR"
			),
			take(1)
		);
	}

	/**
	 * Fetches the emissions table data for the currently selected file.
	 * @private
	 */
	private _setActiveFile(fileId: string | undefined) {
		this._fileId = fileId;
		this._latestEmissionsRows = [];
		if (this._modelEmissionsGridApi && !this._modelEmissionsGridApi.isDestroyed()) {
			this._modelEmissionsGridApi.setGridOption("rowData", []);
		} else {
			this.gridOptions.rowData = [];
		}
		this.modelEmissionsCurrentPage = 0;
		this.modelEmissionsTotalPages = 0;
		this.modelEmissionsTotalElements = 0;
		this.modelEmissionsPageItems = 0;
		this.companyControl.setValue([], { emitEvent: false });
		this.modelControl.setValue([], { emitEvent: false });
		this.waterUsageModelControl.setValue([], { emitEvent: false });
		this._companyFilter$.next(
			fileId ? { fileId, companies: [], refreshOptions: true, showLoading: false } : undefined
		);
		this._modelFilter$.next(
			fileId ? { fileId, models: [], refreshOptions: true, showLoading: false } : undefined
		);
		this._waterModelFilter$.next(
			fileId ? { fileId, models: [], refreshOptions: true, showLoading: false } : undefined
		);
		this.selectedFile.set(this.filesOptions.find(file => file.value === fileId)?.label ?? "");
		this._requestModelEmissionsPage(0);
		this._fileId$.next(fileId);
	}

	private _requestModelEmissionsPage(page: number) {
		this._modelEmissionsRequest$.next(
			this._fileId ? { fileId: this._fileId, page } : undefined
		);
	}

	private _requestCompanyImpact(showLoading: boolean, refreshOptions: boolean) {
		if (!this._fileId) return;
		this._companyFilter$.next({
			fileId: this._fileId,
			companies: (this.companyControl.value as string[]) ?? [],
			refreshOptions,
			showLoading
		});
	}

	private _requestModelImpact(showLoading: boolean, refreshOptions: boolean) {
		if (!this._fileId) return;
		this._modelFilter$.next({
			fileId: this._fileId,
			models: (this.modelControl.value as string[]) ?? [],
			refreshOptions,
			showLoading
		});
	}

	private _requestWaterUsageByModel(showLoading: boolean, refreshOptions: boolean) {
		if (!this._fileId) return;
		this._waterModelFilter$.next({
			fileId: this._fileId,
			models: (this.waterUsageModelControl.value as string[]) ?? [],
			refreshOptions,
			showLoading
		});
	}

	private _updateModelEmissionsGrid(state: {
		isLoading: boolean;
		data?: AiEmissionInfo;
		hasError?: boolean;
	}) {
		if (this._modelEmissionsGridApi && !this._modelEmissionsGridApi.isDestroyed()) {
			this._modelEmissionsGridApi.setGridOption("loading", state.isLoading);
		}
		if (!state.data && !state.hasError) return;
		const response = state.data;
		const normalizedContents = (response?.contents ?? []).map(record => {
			const normalizedWater =
				record.water ??
				record.wu ??
				(record as { waterUsage?: typeof record.water }).waterUsage;
			return { ...record, water: normalizedWater };
		});

		this.modelEmissionsCurrentPage = response?.number ?? this.modelEmissionsCurrentPage;
		this.modelEmissionsTotalPages = response?.totalPages ?? 0;
		this.modelEmissionsTotalElements = response?.totalElements ?? 0;
		this.modelEmissionsPageItems = normalizedContents.length;
		this._latestEmissionsRows = normalizedContents;
		if (this._modelEmissionsGridApi && !this._modelEmissionsGridApi.isDestroyed()) {
			this._modelEmissionsGridApi.setGridOption("rowData", normalizedContents);
			this._refreshModelEmissionsHeaders();
		} else {
			this.gridOptions.rowData = normalizedContents;
		}
	}

	private _handleEnvironmentalImpactResponse(
		impact: EnvironmentalImpact,
		shouldRefreshOptions: boolean
	) {
		const summaryRows = impact.summaryTable ?? [];
		const rawPieChart = impact.pieChart;
		const slices =
			rawPieChart?.PieChartSlice ??
			(rawPieChart as { pieChartSlice?: PieChartSlice[] } | undefined)?.pieChartSlice ??
			[];
		this._totalImpactUnit = slices[0]?.unit ?? "";
		const normalizedPieChart: PieChart | undefined = rawPieChart
			? { ...rawPieChart, PieChartSlice: slices }
			: undefined;
		const totalEmission =
			impact.totalEmission ?? slices.reduce((acc, slice) => acc + (slice.value ?? 0), 0);
		const normalizedTotal = totalEmission || 1;
		const emissionsByCompany = new Map<string, number>(
			slices.map(slice => [slice.name, slice.value])
		);

		const rows: CompanyImpactRow[] = summaryRows.map(summary => ({
			company: summary.company,
			models: summary.modelCount,
			emissionValue: emissionsByCompany.get(summary.company) ?? 0,
			total: normalizedTotal
		}));

		this._latestImpactRows = rows;
		this._updateTotalImpactHeaders();

		if (this._totalImpactGridApi && !this._totalImpactGridApi.isDestroyed()) {
			this._totalImpactGridApi.setGridOption("rowData", rows);
		} else {
			this.totalImpactGridOptions.rowData = rows;
		}

		this.totalImpactTableHasError = rows.length === 0;

		if (shouldRefreshOptions) {
			this.companyOptions = summaryRows.map(summary => ({
				label: summary.company,
				value: summary.company
			}));
		}

		if (!slices.length) {
			this.totalImpactPieChart = { chart: {} as PieChart, hasError: true };
			this.totalImpactPieChartColors = [];
			this.totalImpactPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
			return;
		}

		this.totalImpactPieChart = { chart: normalizedPieChart as PieChart, hasError: false };
		this.totalImpactPieChartColors = slices.map((_, index) =>
			index === slices.length - 1
				? COLORS.GREY_50
				: this._predefinedPieColors[index % this._predefinedPieColors.length]
		);
		this.totalImpactPieChartOverrides = this._buildTotalImpactPieChartOverrides(totalEmission);
	}

	private _handleEnvironmentalImpactError() {
		this.totalImpactTableHasError = true;
		this._latestImpactRows = [];
		if (this._totalImpactGridApi && !this._totalImpactGridApi.isDestroyed()) {
			this._totalImpactGridApi.setGridOption("rowData", []);
		} else {
			this.totalImpactGridOptions.rowData = [];
		}

		this.totalImpactPieChart = { chart: {} as PieChart, hasError: true };
		this.totalImpactPieChartColors = [];
		this.totalImpactPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
		this._totalImpactUnit = "";
		this._updateTotalImpactHeaders();
	}

	private _handleEnvironmentalImpactByModelResponse(
		impact: EnvironmentalImpactByModel,
		shouldRefreshOptions: boolean
	) {
		const summaryRows = impact.summaryTable ?? [];
		const rawPieChart = impact.pieChart;
		const slices =
			rawPieChart?.PieChartSlice ??
			(rawPieChart as { pieChartSlice?: PieChartSlice[] } | undefined)?.pieChartSlice ??
			[];
		const normalizedPieChart: PieChart | undefined = rawPieChart
			? { ...rawPieChart, PieChartSlice: slices }
			: undefined;
		const totalEmission =
			impact.totalEmission ?? slices.reduce((acc, slice) => acc + (slice.value ?? 0), 0);
		const normalizedTotal = totalEmission || 1;
		const emissionsByModel = new Map<string, number>(
			slices.map(slice => [slice.name, slice.value])
		);

		const rows: ModelImpactRow[] = summaryRows.map(summary => ({
			company: summary.company,
			model: summary.model,
			emissionValue: emissionsByModel.get(summary.model) ?? summary.totalEmissions ?? 0,
			emissionPerToken: summary.emissionPerTokenUnit ?? 0,
			total: normalizedTotal
		}));

		this._emissionByModelUnit = impact.emissionUnit;
		this._emissionByModelTokenUnit = impact.emissionTokenUnit;
		this._updateByModelHeaders();

		this._latestByModelRows = rows;

		if (this._byModelGridApi && !this._byModelGridApi.isDestroyed()) {
			this._byModelGridApi.setGridOption("rowData", rows);
		} else {
			this.byModelGridOptions.rowData = rows;
		}

		this.byModelTableHasError = rows.length === 0;

		if (shouldRefreshOptions) {
			this.modelOptions = summaryRows.map(summary => ({
				label: summary.model,
				value: summary.model
			}));
		}

		if (!slices.length) {
			this.byModelPieChart = { chart: {} as PieChart, hasError: true };
			this.byModelPieChartColors = [];
			this.byModelPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
			return;
		}

		this.byModelPieChart = { chart: normalizedPieChart as PieChart, hasError: false };
		this.byModelPieChartColors = slices.map((_, index) =>
			index === slices.length - 1
				? COLORS.GREY_50
				: this._predefinedPieColors[index % this._predefinedPieColors.length]
		);
		this.byModelPieChartOverrides = this._buildTotalImpactPieChartOverrides(totalEmission);
	}

	private _handleEnvironmentalImpactByModelError() {
		this.byModelTableHasError = true;
		this._latestByModelRows = [];
		if (this._byModelGridApi && !this._byModelGridApi.isDestroyed()) {
			this._byModelGridApi.setGridOption("rowData", []);
		} else {
			this.byModelGridOptions.rowData = [];
		}

		this.byModelPieChart = { chart: {} as PieChart, hasError: true };
		this.byModelPieChartColors = [];
		this.byModelPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
	}

	private _handleWaterUsageByModelResponse(
		data: WaterUsageByModel,
		shouldRefreshOptions: boolean
	) {
		const summaryRows = data.summaryTable ?? [];
		const rawPieChart = data.pieChart;
		const slices = rawPieChart?.pieChartSlice ?? [];
		const normalizedPieChart: PieChart | undefined = rawPieChart
			? { ...rawPieChart, PieChartSlice: slices }
			: undefined;
		const totalWaterUsage =
			data.totalWaterUsage ?? slices.reduce((acc, slice) => acc + (slice.value ?? 0), 0);
		const normalizedTotal = totalWaterUsage || 1;

		const rows: WaterUsageByModelRow[] = summaryRows.map(summary => ({
			model: summary.model,
			company: summary.company,
			totalWaterUsage: summary.totalWaterUsage,
			waterUsagePerToken: summary.waterUsagePerTokenUnit ?? 0,
			total: normalizedTotal
		}));

		this._waterByModelUnit = data.waterUnit;
		this._waterByModelTokenUnit = data.waterTokenUnit;
		this._updateWaterUsageByModelHeaders();

		this._latestWaterUsageByModelRows = rows;

		if (this._waterUsageByModelGridApi && !this._waterUsageByModelGridApi.isDestroyed()) {
			this._waterUsageByModelGridApi.setGridOption("rowData", rows);
		} else {
			this.waterUsageByModelGridOptions.rowData = rows;
		}

		this.waterUsageByModelTableHasError = rows.length === 0;

		if (shouldRefreshOptions) {
			this.waterUsageModelOptions = summaryRows.map(summary => ({
				label: summary.model,
				value: summary.model
			}));
		}

		if (!slices.length) {
			this.waterUsageByModelPieChart = { chart: {} as PieChart, hasError: true };
			this.waterUsageByModelPieChartColors = [];
			this.waterUsageByModelPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
			return;
		}

		this.waterUsageByModelPieChart = { chart: normalizedPieChart as PieChart, hasError: false };
		this.waterUsageByModelPieChartColors = slices.map((_, index) =>
			index === slices.length - 1
				? COLORS.GREY_50
				: this._predefinedPieColors[index % this._predefinedPieColors.length]
		);
		this.waterUsageByModelPieChartOverrides =
			this._buildTotalImpactPieChartOverrides(totalWaterUsage);
	}

	private _handleWaterUsageByModelError() {
		this.waterUsageByModelTableHasError = true;
		this._latestWaterUsageByModelRows = [];
		if (this._waterUsageByModelGridApi && !this._waterUsageByModelGridApi.isDestroyed()) {
			this._waterUsageByModelGridApi.setGridOption("rowData", []);
		} else {
			this.waterUsageByModelGridOptions.rowData = [];
		}

		this.waterUsageByModelPieChart = { chart: {} as PieChart, hasError: true };
		this.waterUsageByModelPieChartColors = [];
		this.waterUsageByModelPieChartOverrides = this._buildTotalImpactPieChartOverrides(0);
	}

	private _onWaterUsageByModelGridReady(params: GridReadyEvent) {
		this._waterUsageByModelGridApi = params.api;

		if (
			!this._waterUsageByModelGridApi.isDestroyed() &&
			this._latestWaterUsageByModelRows.length
		) {
			this._waterUsageByModelGridApi.setGridOption(
				"rowData",
				this._latestWaterUsageByModelRows
			);
		}

		this._updateWaterUsageByModelHeaders();
	}

	private _onByModelGridReady(params: GridReadyEvent) {
		this._byModelGridApi = params.api;

		if (!this._byModelGridApi.isDestroyed() && this._latestByModelRows.length) {
			this._byModelGridApi.setGridOption("rowData", this._latestByModelRows);
		}

		this._updateByModelHeaders();
	}

	private _onModelEmissionsGridReady(params: GridReadyEvent) {
		this._modelEmissionsGridApi = params.api;

		if (!this._modelEmissionsGridApi.isDestroyed() && this._latestEmissionsRows.length) {
			this._modelEmissionsGridApi.setGridOption("rowData", this._latestEmissionsRows);
		}

		this._refreshModelEmissionsHeaders();
	}

	private _refreshModelEmissionsHeaders() {
		if (this._modelEmissionsGridApi && !this._modelEmissionsGridApi.isDestroyed()) {
			this._modelEmissionsGridApi.refreshHeader();
		}
	}

	private _getModelEmissionsColumnHeader(
		label: string,
		metric: "energy" | "emission" | "water"
	): string {
		const unit = this._getModelEmissionsUnit(metric);
		return unit ? `${label} (${unit})` : label;
	}

	private _getModelEmissionsUnit(metric: "energy" | "emission" | "water"): string | undefined {
		for (const row of this._latestEmissionsRows) {
			const value =
				metric === "water" ? (row.water ?? row.wu ?? row.waterUsage) : row[metric];
			if (value?.unit) {
				return value.unit;
			}
		}

		return undefined;
	}

	private _onTotalImpactGridReady(params: GridReadyEvent) {
		this._totalImpactGridApi = params.api;

		if (!this._totalImpactGridApi.isDestroyed() && this._latestImpactRows.length) {
			this._totalImpactGridApi.setGridOption("rowData", this._latestImpactRows);
		}

		this._updateTotalImpactHeaders();
	}

	private _buildLineChartOverrides(yAxisName: string): EChartsOption {
		return {
			grid: {
				left: 48,
				right: 24,
				bottom: 64,
				top: 72
			},
			legend: {
				itemHeight: 8,
				itemWidth: 24,
				textStyle: {
					color: COLORS.GREY_50,
					fontSize: 14
				}
			},
			yAxis: {
				name: yAxisName,
				axisLine: { show: false },
				axisTick: { show: false },
				splitLine: {
					lineStyle: {
						color: COLORS.GREY_100,
						width: 0.5
					}
				},
				axisLabel: {
					color: COLORS.GREY_50,
					fontSize: 10
				},
				nameTextStyle: {
					color: COLORS.GREY_50,
					fontSize: 10,
					padding: [0, 0, 20, -32]
				}
			},
			xAxis: {
				boundaryGap: false,
				axisLine: {
					lineStyle: {
						color: COLORS.GREY_100
					}
				},
				axisTick: {
					alignWithLabel: true,
					length: 8,
					lineStyle: {
						color: COLORS.WHITE
					}
				},
				axisLabel: {
					color: COLORS.WHITE,
					fontSize: 12,
					formatter: (value: string | number) => this._formatAxisMonthYear(value)
				},
				splitLine: { show: false }
			},
			tooltip: {
				trigger: "item",
				formatter: (params: TopLevelFormatterParams) =>
					getChartTooltip(params as CallbackDataParams)
			},
			series: {
				lineStyle: {
					width: 2
				},
				symbolSize: 8
			}
		};
	}

	private _buildTotalImpactPieChartOverrides(total: number): EChartsOption {
		const baseOverrides = getHardwareAssetsTotalEmissionOverridesForPieChart(
			roundValueUpTo(total)
		) as EChartsOption & {
			graphic?: { style?: { text?: string } }[];
		};

		return {
			...baseOverrides,
			graphic: baseOverrides.graphic?.map((item, index) =>
				index === 1
					? {
							...item,
							style: {
								...item.style,
								text: this._formatTotalImpactEmissionValue(total)
							}
						}
					: item
			)
		};
	}

	private _formatTotalImpactEmissionValue(value: number): string {
		if (!Number.isFinite(value)) {
			return "";
		}

		if (value === 0) {
			return "0.00";
		}

		if (Math.abs(value) < 0.01) {
			return value > 0 ? "<.01" : "-<.01";
		}

		return new Intl.NumberFormat("en-US", {
			minimumFractionDigits: 2,
			maximumFractionDigits: 2,
			useGrouping: false
		}).format(value);
	}

	private _formatAxisMonthYear(value: string | number): string {
		if (typeof value === "string" && /^\d{2}-\d{4}$/.test(value)) {
			return value;
		}

		const dateValue = typeof value === "number" ? new Date(value) : new Date(value);

		if (Number.isNaN(dateValue.getTime())) {
			return String(value);
		}

		const month = String(dateValue.getUTCMonth() + 1).padStart(2, "0");
		const year = String(dateValue.getUTCFullYear());

		return `${month}-${year}`;
	}
}
